import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Library, DoorOpen, Bell, BookOpen, Copy, RefreshCw, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { useAuth } from "../../context/AuthContext";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [joinCode, setJoinCode] = useState(user.join_code);

  useEffect(() => {
    api.get("/school/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const regenerate = async () => {
    try {
      const { data } = await api.post("/school/regenerate-join-code");
      setJoinCode(data.join_code);
      toast.success("New join code generated. Old code no longer works for new pupils.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const cards = [
    { label: "Pupils", value: stats?.pupils, icon: Users, to: "/admin/pupils", bg: "#A7E4D3" },
    { label: "Teachers", value: stats?.teachers, icon: Library, to: "/admin/directory", bg: "#FDE68A" },
    { label: "Subjects", value: stats?.subjects, icon: BookOpen, to: "/admin/directory", bg: "#7DD3FC" },
    { label: "Rooms", value: stats?.rooms, icon: DoorOpen, to: "/admin/directory", bg: "#F9A8D4" },
    { label: "Notices sent", value: stats?.notices, icon: Bell, to: "/admin/notices", bg: "#D8B4FE" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <span className="nb-label">Overview</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">
        Hello, {user.name}
      </h1>

      <div className="mt-8 nb-card p-6 lg:p-8 bg-[#1E3A8A] text-white flex flex-col sm:flex-row sm:items-center gap-6 justify-between" data-testid="join-code-card">
        <div>
          <span className="text-xs tracking-[0.15em] uppercase font-bold text-blue-200">Pupil join code</span>
          <div className="font-display font-black text-4xl tracking-[0.2em] mt-2" data-testid="join-code-display">{joinCode}</div>
          <p className="text-blue-100 text-sm mt-2 max-w-md">Share this code with pupils. They use it with their name and a PIN they choose to log in.</p>
        </div>
        <div className="flex gap-3 shrink-0">
          <button onClick={async () => { const ok = await copyToClipboard(joinCode); ok ? toast.success("Copied!") : toast.error("Couldn't copy — select the code manually"); }} data-testid="dashboard-copy-code-btn" className="nb-btn bg-[#FDE68A] text-zinc-900 px-4 py-2 text-sm">
            <Copy className="w-4 h-4" /> Copy
          </button>
          <button onClick={regenerate} data-testid="regenerate-code-btn" className="nb-btn bg-white px-4 py-2 text-sm text-zinc-900">
            <RefreshCw className="w-4 h-4" /> New code
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-5">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="nb-card p-5 hover:-translate-y-1 transition-transform duration-200 block">
            <div className="w-10 h-10 rounded-xl border-2 border-zinc-900 flex items-center justify-center mb-3" style={{ backgroundColor: c.bg }}>
              <c.icon className="w-5 h-5" />
            </div>
            <div className="font-display font-black text-3xl" data-testid={`stat-value-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>{c.value ?? "–"}</div>
            <div className="text-sm font-bold text-zinc-500">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-8 nb-card p-6 lg:p-8">
        <h2 className="font-display font-bold text-xl">Getting started</h2>
        <ol className="mt-4 space-y-3">
          {[
            ["Customise your schedule layout", "Define periods, breaks, lunch — plus early-finish Fridays.", "/admin/schedule", !!stats?.schedule_customised],
            ["Build your directory", "Add teachers, subjects and rooms pupils can pick from (CSV import works too).", "/admin/directory", !!stats && stats.teachers > 0 && stats.subjects > 0],
            ["Get your first pupil", "Share the join code above — pupils sign up with their name and a PIN.", "/admin/pupils", !!stats && stats.pupils > 0],
            ["Send a notice", "Push a message to the whole school or one year group.", "/admin/notices", !!stats && stats.notices > 0],
          ].map(([t, d, to, done], i) => (
            <li key={t}>
              <Link to={to} data-done={done} className={`flex items-center gap-4 rounded-xl border-2 p-4 transition-colors duration-150 group ${done ? "border-zinc-900 bg-[#A7E4D3]/30" : "border-zinc-200 hover:border-zinc-900"}`} data-testid={`getting-started-${i}`}>
                <span className={`w-8 h-8 shrink-0 rounded-lg border-2 border-zinc-900 font-display font-black flex items-center justify-center ${done ? "bg-[#A7E4D3]" : "bg-white"}`} data-testid={`getting-started-tick-${i}`}>
                  {done ? <Check className="w-4 h-4" /> : i + 1}
                </span>
                <span className="flex-1">
                  <span className="font-bold block">{t}</span>
                  <span className="text-sm text-zinc-500">{d}</span>
                </span>
                <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-zinc-900 transition-colors duration-150" />
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
